import { useMemo } from 'react';
import { getOrg } from '~/src/entities/user';
import { WELCOME_SURVEY_ENFORCEMENT_DATE } from '~/src/entities/welcomeForm/constants';
import {
  Organization,
  Membership,
} from '~/src/entities/user/hooks/useMemberships';

type OrgWithMembership = Organization & Membership;

const isWelcomeSurveyCompleted = (org: OrgWithMembership): boolean => {
  // Check if user has completed the welcome survey by verifying that both
  // primary_practice_area and primary_state are filled out
  const hasPrimaryPracticeArea =
    org.primary_practice_area && org.primary_practice_area.trim() !== '';
  const hasPrimaryState = org.primary_state && org.primary_state.trim() !== '';

  return !!(hasPrimaryPracticeArea && hasPrimaryState);
};

const isOrgCreatedAfterWelcomeSurveyDate = (
  org: OrgWithMembership,
): boolean => {
  if (!org?.org_created_at) {
    // If no creation date, assume it's an old org and don't enforce
    return false;
  }

  const orgCreationDate = new Date(org.org_created_at);
  return orgCreationDate >= WELCOME_SURVEY_ENFORCEMENT_DATE;
};

export const useWelcomeSurveyRedirect = (): boolean => {
  const org = getOrg();
  const isAdmin = org?.admin;

  const shouldRedirect = useMemo(() => {
    // If org doesn't exist or user is not admin, don't redirect
    if (!org || !isAdmin) {
      return false;
    }

    if (!isOrgCreatedAfterWelcomeSurveyDate(org)) {
      return false;
    }

    // Check if welcome survey needs to be completed
    const needsWelcomeSurvey = !isWelcomeSurveyCompleted(org);
    return needsWelcomeSurvey;
  }, [org, isAdmin]);

  return shouldRedirect;
};

export default useWelcomeSurveyRedirect;
